import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  api, money, shortDate,
  type LoanDetail, type LoanPurpose, type LoanSummary, type PropertyDetail, type PropertySummary, type PropertyUse, type Reading, type RentalYear, type SettlementLine,
} from "../api";
import { Choice, ErrorNotice, InlineForm, Loading } from "../ui";

const toCents = (text: string | undefined): number | null => {
  const clean = (text ?? "").replace(/[$,\s]/g, "");
  if (!clean) return null;
  const n = Number(clean);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : NaN;
};
const needCents = (text: string | undefined, what: string) => {
  const c = toCents(text);
  if (c === null || Number.isNaN(c)) throw new Error(`Enter ${what} as a number, like 1250.00`);
  return c;
};
const today = () => new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });
const gap = (px: number) => ({ "--gap": `${px}px` }) as React.CSSProperties;

export const USE_LABEL: Record<PropertyUse, string> = {
  rented: "Rented",
  available: "Available for rent",
  main_residence: "Your home",
  private: "Used privately (e.g. holiday home)",
  vacant: "Vacant, not available for rent",
};
const USE_OPTIONS = (Object.keys(USE_LABEL) as PropertyUse[]).map((value) => ({ value, label: USE_LABEL[value] }));
const KIND_LABEL: Record<string, string> = { house: "House", unit: "Unit or apartment", townhouse: "Townhouse", land: "Vacant land", other: "Other" };
const ELEMENT_LABEL: Record<number, string> = {
  2: "Buying or selling costs (stamp duty, legal, agent)",
  3: "Holding costs you couldn't claim",
  4: "Improvements (renovations, extensions)",
  5: "Costs of defending your title",
};

// ---- List -------------------------------------------------------------------------------------

export function Properties() {
  const [properties, setProperties] = useState<PropertySummary[] | null>(null);
  const [loans, setLoans] = useState<LoanSummary[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    api.get<PropertySummary[]>("/api/properties").then(setProperties).catch(setError);
    api.get<LoanSummary[]>("/api/loans").then(setLoans).catch(setError);
  }, []);
  return (
    <div className="stack" style={gap(24)}>
      <div className="row wrap">
        <div>
          <h1>Properties</h1>
          <p className="muted">Rental properties and your home: who owns them, how they're used, their loans, and each year's rental schedule.</p>
        </div>
        <div className="spacer" />
        <Link to="/properties/new" className="btn primary">+ Add a property</Link>
      </div>
      <ErrorNotice error={error} />
      {!properties ? <Loading /> : properties.length === 0 ? (
        <div className="card flat empty">No properties yet. Add a rental property to start claiming its expenses.</div>
      ) : (
        <div className="card list-card list">
          {properties.map((p) => (
            <Link key={p.id} to={`/properties/${p.id}`} className="list-item">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{p.name}{p.soldOn && <span className="pill" style={{ marginLeft: 8 }}>Sold {shortDate(p.soldOn)}</span>}</div>
                <div className="muted small">{p.address || KIND_LABEL[p.kind]}</div>
              </div>
              <div className="muted small" style={{ textAlign: "right" }}>
                {p.currentUse ? USE_LABEL[p.currentUse] : "Use not set"}
                {p.yourSharePct > 0 && p.yourSharePct < 100 && <div>You own {p.yourSharePct}%</div>}
              </div>
              <span aria-hidden className="muted">›</span>
            </Link>
          ))}
        </div>
      )}

      <section className="stack">
        <div className="row wrap"><h2>Loans</h2><div className="spacer" /><Link to="/loans/new" className="btn">+ Add a loan</Link></div>
        {!loans ? <Loading /> : loans.length === 0 ? <p className="muted">No loans yet.</p> : (
          <div className="card list-card list">
            {loans.map((l) => (
              <Link key={l.id} to={`/loans/${l.id}`} className="list-item">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{l.name}{l.closedOn && <span className="pill" style={{ marginLeft: 8 }}>Closed {shortDate(l.closedOn)}</span>}</div>
                  <div className="muted small">{l.lender ?? "Loan"} · from {shortDate(l.startedOn)}</div>
                </div>
                <div className="muted small" style={{ textAlign: "right" }}>{l.purposes.map((x) => `${x.name} ${x.pct}%`).join(" · ")}</div>
                <span aria-hidden className="muted">›</span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ---- Add a property ---------------------------------------------------------------------------

export function AddProperty() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [v, setV] = useState<Record<string, string>>({ kind: "house", yourPct: "100" });
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setV({ ...v, [k]: e.target.value });
  const [use, setUse] = useState<PropertyUse | null>(null);
  const [wasHome, setWasHome] = useState<"yes" | "no">("no");
  const [coOwners, setCoOwners] = useState<{ name: string; pct: string }[]>([]);
  const [hasSchedule, setHasSchedule] = useState<"yes" | "no">("no");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const ownersTotal = Number(v.yourPct || 0) + coOwners.reduce((s, o) => s + Number(o.pct || 0), 0);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const property = await api.post<PropertyDetail>("/api/properties", {
        name: v.name!.trim(), address: v.address?.trim() || null, state: v.state || null, postcode: v.postcode?.trim() || null, kind: v.kind,
        contractOn: v.contractOn, settledOn: v.settledOn, purchasePriceCents: needCents(v.price, "the price"),
        stampDutyCents: toCents(v.stampDuty) ?? undefined, legalCents: toCents(v.legal) ?? undefined, otherPurchaseCostsCents: toCents(v.otherCosts) ?? undefined,
        owners: { yourPct: Number(v.yourPct), others: coOwners.map((o) => ({ name: o.name.trim(), pct: Number(o.pct) })) },
        use: wasHome === "yes" ? "main_residence" : use,
        firstIncomeUse: wasHome === "yes" ? { on: v.firstRentedOn, valueCents: needCents(v.marketValue, "the market value") } : null,
        building: hasSchedule === "yes" ? {
          constructionStartedOn: v.constructionStartedOn, completedOn: v.completedOn, costCents: needCents(v.constructionCost, "the construction cost"),
          claimedBeforeCents: toCents(v.claimedBefore) ?? 0,
        } : null,
      });
      // A former home that's now rented: record the change of use too.
      if (wasHome === "yes" && use && use !== "main_residence") {
        await api.post(`/api/properties/${property.id}/use`, { use, from: v.firstRentedOn, marketValueCents: needCents(v.marketValue, "the market value") });
      }
      navigate(`/properties/${property.id}`, { replace: true });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const field = (id: string, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}, hint?: string) => (
    <div className="field"><label htmlFor={`p-${id}`}>{label}</label><input id={`p-${id}`} type="text" value={v[id] ?? ""} onChange={set(id)} {...props} />{hint && <span className="hint">{hint}</span>}</div>
  );

  const steps = [
    {
      title: "About the property",
      valid: Boolean(v.name?.trim()),
      body: (
        <>
          {field("name", "What do you call it?", { placeholder: "e.g. Unit 4, the beach house", autoFocus: true })}
          {field("address", "Address", { placeholder: "Street address" }, "Optional, but it goes on your rental schedule.")}
          <div className="fields-2">
            <div className="field"><label htmlFor="p-state">State</label>
              <select id="p-state" value={v.state ?? ""} onChange={set("state")}><option value="">—</option>{["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"].map((s) => <option key={s}>{s}</option>)}</select>
            </div>
            {field("postcode", "Postcode", { inputMode: "numeric", maxLength: 4 })}
          </div>
          <div className="field"><label htmlFor="p-kind">Type</label>
            <select id="p-kind" value={v.kind} onChange={set("kind")}>{Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
          </div>
        </>
      ),
    },
    {
      title: "Buying it",
      valid: Boolean(v.contractOn && v.settledOn && v.price?.trim()),
      body: (
        <>
          <div className="fields-2">
            {field("contractOn", "Contract date", { type: "date", max: today() }, "The day you signed — it decides the year for capital gains.")}
            {field("settledOn", "Settlement date", { type: "date", min: v.contractOn }, "When it became yours.")}
          </div>
          {field("price", "Purchase price", { inputMode: "decimal", placeholder: "650000" })}
          <p className="muted small">Costs of buying are added to its cost base, which lowers any capital gain when you sell. Your settlement statement has them.</p>
          <div className="fields-2">
            {field("stampDuty", "Stamp duty on the transfer", { inputMode: "decimal" })}
            {field("legal", "Conveyancing and legal fees", { inputMode: "decimal" })}
          </div>
          {field("otherCosts", "Other buying costs (inspections, buyer's agent)", { inputMode: "decimal" })}
          <p className="muted small">Loan set-up costs (establishment fees, mortgage insurance, mortgage stamp duty) go on the loan instead.</p>
        </>
      ),
    },
    {
      title: "Who owns it",
      valid: Math.abs(ownersTotal - 100) < 0.001 && coOwners.every((o) => o.name.trim() && Number(o.pct) > 0),
      body: (
        <>
          <div className="field" style={{ maxWidth: 200 }}><label htmlFor="p-yourPct">Your share (%)</label><input id="p-yourPct" type="number" min={1} max={100} value={v.yourPct} onChange={set("yourPct")} /></div>
          {coOwners.map((o, i) => (
            <div className="fields-2" key={i}>
              <div className="field"><label htmlFor={`co-${i}`}>Co-owner's name</label><input id={`co-${i}`} type="text" value={o.name} onChange={(e) => setCoOwners(coOwners.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} /></div>
              <div className="field"><label htmlFor={`co-pct-${i}`}>Their share (%)</label><input id={`co-pct-${i}`} type="number" value={o.pct} onChange={(e) => setCoOwners(coOwners.map((x, j) => (j === i ? { ...x, pct: e.target.value } : x)))} /></div>
            </div>
          ))}
          <div className="row">
            <button type="button" className="btn" onClick={() => { setCoOwners([...coOwners, { name: "", pct: String(Math.max(0, 100 - ownersTotal)) }]); }}>+ Add a co-owner</button>
            {coOwners.length > 0 && <button type="button" className="btn ghost" onClick={() => setCoOwners(coOwners.slice(0, -1))}>Remove</button>}
          </div>
          {Math.abs(ownersTotal - 100) >= 0.001 && <div className="notice warn">The shares add up to {ownersTotal}%. They need to add up to 100%.</div>}
          <p className="muted small">Each receipt is entered once, in full; your return gets your share. Joint owners usually split by their legal ownership, even if one pays more.</p>
        </>
      ),
    },
    {
      title: "How it's used",
      valid: use !== null && (wasHome === "no" || Boolean(v.firstRentedOn && v.marketValue?.trim())),
      body: (
        <>
          <div className="question">
            <span className="label">How is it used now?</span>
            <Choice label="Use" value={use} onChange={setUse} options={USE_OPTIONS} />
          </div>
          {use && use !== "main_residence" && (
            <div className="question">
              <span className="label">Was it your home before you started renting it?</span>
              <Choice label="Was it your home" value={wasHome} onChange={setWasHome} options={[{ value: "no", label: "No" }, { value: "yes", label: "Yes" }]} />
            </div>
          )}
          {wasHome === "yes" && use !== "main_residence" && (
            <>
              <div className="fields-2">
                {field("firstRentedOn", "Date you first rented it out", { type: "date", min: v.settledOn, max: today() })}
                {field("marketValue", "Its market value on that day", { inputMode: "decimal" }, "From a valuation. It becomes its cost for capital gains.")}
              </div>
            </>
          )}
        </>
      ),
    },
    {
      title: "The building",
      valid: hasSchedule === "no" || Boolean(v.constructionStartedOn && v.completedOn && v.constructionCost?.trim()),
      body: (
        <>
          <p className="muted">The building itself is claimed over 40 years as capital works (usually 2.5% of what it cost to build). A quantity surveyor's depreciation schedule gives the figures.</p>
          <div className="question">
            <span className="label">Do you have a depreciation schedule?</span>
            <Choice label="Depreciation schedule" value={hasSchedule} onChange={setHasSchedule} options={[{ value: "yes", label: "Yes" }, { value: "no", label: "Not yet" }]} />
          </div>
          {hasSchedule === "yes" && (
            <>
              <div className="fields-2">
                {field("constructionStartedOn", "Construction started", { type: "date" })}
                {field("completedOn", "Construction finished", { type: "date" })}
              </div>
              <div className="fields-2">
                {field("constructionCost", "Construction cost (capital works)", { inputMode: "decimal" })}
                {field("claimedBefore", "Capital works already claimed", { inputMode: "decimal" }, "Only if you owned it before July 2024: the total claimed up to then. Later years are worked out for you.")}
              </div>
            </>
          )}
          {hasSchedule === "no" && <p className="muted small">You can add it later. A task will remind you.</p>}
        </>
      ),
    },
  ];
  const current = steps[step]!;
  const last = steps.length - 1;

  return (
    <div className="wizard stack" style={gap(24)}>
      <div className="row"><Link to="/properties" className="btn ghost">← Properties</Link><div className="spacer" /><span className="muted small">Step {step + 1} of {steps.length}</span></div>
      <div className="progress" aria-hidden><div style={{ width: `${((step + 1) / steps.length) * 100}%` }} /></div>
      <h1>{current.title}</h1>
      <div className="stack" style={gap(16)}>{current.body}</div>
      <ErrorNotice error={error} />
      <div className="wizard-actions">
        {step > 0 && <button type="button" className="btn ghost" onClick={() => setStep(step - 1)} disabled={busy}>Back</button>}
        <div className="spacer" />
        {step < last
          ? <button type="button" className="btn primary" disabled={!current.valid} onClick={() => setStep(step + 1)}>Continue</button>
          : <button type="button" className="btn primary" disabled={!current.valid || busy} onClick={save}>{busy ? "Adding…" : "Add property"}</button>}
      </div>
    </div>
  );
}

// ---- One property -----------------------------------------------------------------------------

function RentalYearCard({ y }: { y: RentalYear }) {
  const groups: [RentalYear["lines"][number]["group"], string][] = [["income", "Income"], ["interest", "Interest"], ["capital_works", "Capital works"], ["other", "Other deductions"], ["not_deductible", "Not claimed as expenses"]];
  return (
    <div className="card stack" style={gap(12)}>
      <div className="row wrap">
        <h3>{y.label}</h3>
        {y.daysHeld < 365 && <span className="pill">{y.daysHeld} days owned</span>}
        {y.sharePct < 100 && <span className="pill">Your share {y.sharePct}%</span>}
        <div className="spacer" />
        <strong style={{ color: y.net < 0 ? "var(--accent)" : undefined }}>{y.net < 0 ? "Net loss " : "Net income "}{money(Math.abs(y.net))}</strong>
      </div>
      {y.labels && (
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12 }}>
          {([["P", "Gross rent"], ["Q", "Interest"], ["F", "Capital works"], ["U", "Other deductions"]] as const).map(([k, label]) => (
            <div className="stat" key={k}><span className="muted small">21{k} · {label}</span><strong>${y.labels![k].toLocaleString("en-AU")}</strong></div>
          ))}
        </div>
      )}
      <details className="small">
        <summary>What's included</summary>
        <div className="stack" style={gap(10)}>
          {groups.map(([g, title]) => {
            const lines = y.lines.filter((l) => l.group === g);
            if (lines.length === 0) return null;
            return (
              <div key={g} className="stack" style={gap(4)}>
                <strong>{title}</strong>
                {lines.map((l, i) => (
                  <div key={i}>
                    <div className="row"><span>{l.label}</span><div className="spacer" /><span className="amount">{money(l.cents)}</span></div>
                    <div className="muted small">{l.explanation}</div>
                  </div>
                ))}
              </div>
            );
          })}
          {y.lines.length === 0 && <span className="muted">Nothing filed to this property for {y.label} yet.</span>}
        </div>
      </details>
      {y.checks.map((c, i) => <div key={i} className={`notice${c.level === "warning" ? " warn" : ""}`}>{c.message}</div>)}
    </div>
  );
}

export function PropertyDetailPage() {
  const { id } = useParams();
  const [p, setP] = useState<PropertyDetail | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    api.get<PropertyDetail>(`/api/properties/${id}`).then(setP).catch(setError);
  }, [id]);
  if (!p) return <><ErrorNotice error={error} /><Loading /></>;

  const cg = p.sale?.capitalGain;
  const everHome = p.useHistory.some((u) => u.use === "main_residence");
  const others = p.owners.filter((o) => !o.you && !o.to);
  return (
    <div className="stack" style={gap(24)}>
      <div className="row wrap"><Link to="/properties" className="btn ghost">← Properties</Link></div>
      <div className="row wrap">
        <div>
          <h1>{p.name}</h1>
          <p className="muted">
            {[p.address, p.state, p.postcode].filter(Boolean).join(" ") || KIND_LABEL[p.kind]} · bought {shortDate(p.contractOn)} for {money(p.purchasePriceCents)}
            {p.sale && ` · sold ${shortDate(p.sale.contractOn)} for ${money(p.sale.priceCents)}`}
          </p>
        </div>
        <div className="spacer" />
        <div className="stat" style={{ textAlign: "right" }}>
          <span className="muted small">{p.sale ? "Was" : "Now"}</span>
          <strong style={{ fontSize: 20 }}>{p.currentUse ? USE_LABEL[p.currentUse] : p.sale ? "Sold" : "Use not set"}</strong>
          <span className="muted small">You own {p.yourSharePct}%{others.length > 0 ? ` with ${others.map((o) => `${o.name} ${o.pct}%`).join(", ")}` : ""}</span>
        </div>
      </div>

      {p.sale && (
        <section className="card stack" style={gap(8)}>
          <h2>Sale</h2>
          {p.sale.problem && <div className="notice warn">{p.sale.problem}</div>}
          {cg && (
            <>
              {cg.status === "exempt" && <p><strong>No capital gains tax</strong> — the main residence exemption covers it.</p>}
              {cg.status !== "exempt" && cg.gain > 0 && <p>Capital gain <strong>{money(cg.gain)}</strong> for {cg.eventFy.replace("FY", "")}{cg.discountEligible ? <>, <strong>{money(cg.discountedGain)}</strong> after the 50% discount (if you have no capital losses to use first)</> : ""}.</p>}
              {cg.loss > 0 && <p>Capital loss <strong>{money(cg.loss)}</strong>, carried forward against future gains.</p>}
              {cg.status === "estimate" && <div className="notice warn">This is an estimate: the main residence exemption applies to part of the time. Check the choices with your accountant.</div>}
              <details className="small muted"><summary>How it was worked out</summary><ol>{cg.steps.map((s, i) => <li key={i}>{s}</li>)}</ol></details>
            </>
          )}
        </section>
      )}

      <section className="stack">
        <h2>Year by year</h2>
        {[...p.years].reverse().map((y) => <RentalYearCard key={y.fy} y={y} />)}
        <p className="muted small">Rent and expenses come from what you file to this property in To review. Enter each bill in full; your share is worked out for you.</p>
      </section>

      <section className="stack">
        <h2>How it's been used</h2>
        <div className="card list-card list">
          {p.useHistory.map((u) => (
            <div key={u.from} className="list-item"><span style={{ flex: 1 }}>{USE_LABEL[u.use]}</span><span className="muted small">{shortDate(u.from)} – {u.to ? shortDate(u.to) : "now"}</span></div>
          ))}
        </div>
        {!p.sale && (
          <InlineForm<PropertyDetail> title="Something changed (moved in, moved out, started renting)" onDone={setP}
            fields={[
              { id: "use", label: "It's now", type: "select", options: USE_OPTIONS },
              { id: "from", label: "From", type: "date" },
              ...(p.currentUse === "main_residence" && !p.firstIncomeUse ? [{ id: "value", label: "Market value that day, if you're starting to rent it", type: "text", optional: true, hint: "Needed if it's rented for the first time after being your home." }] : []),
            ]}
            submit={(v) => api.post(`/api/properties/${p.id}/use`, { use: v.use, from: v.from, marketValueCents: toCents(v.value) })} />
        )}
        {everHome && (
          <label className="row small" style={{ gap: 8 }}>
            <input type="checkbox" checked={p.absenceChoice} onChange={async (e) => setP(await api.patch<PropertyDetail>(`/api/properties/${p.id}`, { absenceChoice: e.target.checked }))} />
            Keep treating it as my home while I'm away (the 6-year absence rule). You can only have one main residence at a time.
          </label>
        )}
        {p.firstIncomeUse && <p className="muted small">First rented {shortDate(p.firstIncomeUse.on)}, when it was worth {money(p.firstIncomeUse.valueCents)}.</p>}
      </section>

      <section className="stack">
        <div className="row wrap"><h2>Loans</h2><div className="spacer" />{!p.sale && <Link to={`/loans/new?property=${p.id}`} className="btn">+ Add a loan</Link>}</div>
        {p.loans.length === 0 ? <p className="muted">No loans linked to this property.</p> : (
          <div className="card list-card list">
            {p.loans.map((l) => (
              <Link key={l.id} to={`/loans/${l.id}`} className="list-item">
                <span style={{ flex: 1 }}>{l.name}<span className="muted small">{l.lender ? ` · ${l.lender}` : ""}{l.closedOn ? ` · closed ${shortDate(l.closedOn)}` : ""}</span></span>
                <span className="muted small">{l.purposePct}% for this property</span><span aria-hidden className="muted">›</span>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="stack">
        <h2>Capital works</h2>
        {p.capitalWorks.length === 0 ? <p className="muted">No building costs yet. Add them from the depreciation schedule.</p> : (
          <div className="card list-card list">
            {p.capitalWorks.map((w) => (
              <div key={w.id} className="list-item">
                <div style={{ flex: 1 }}>{w.description}<div className="muted small">Built {shortDate(w.constructionStartedOn)} – {shortDate(w.completedOn)}{w.rateOverride ? ` · ${Number(w.rateOverride) * 100}% rate` : ""}</div></div>
                <div style={{ textAlign: "right" }}><span className="amount">{money(w.costCents)}</span><div className="muted small">{money(w.claimedToDateCents)} claimed to date</div></div>
              </div>
            ))}
          </div>
        )}
        {!p.sale && (
          <InlineForm<PropertyDetail> title="Add capital works" onDone={setP}
            fields={[
              { id: "description", label: "What", type: "text" },
              { id: "cost", label: "Construction cost", type: "text" },
              { id: "started", label: "Construction started", type: "date" },
              { id: "completed", label: "Construction finished", type: "date" },
              { id: "claimed", label: "Claimed before July 2024", type: "text", optional: true, hint: "Only if it was rented before then." },
            ]}
            submit={(v) => api.post(`/api/properties/${p.id}/capital-works`, {
              description: v.description, costCents: needCents(v.cost, "the cost"), constructionStartedOn: v.started, completedOn: v.completed, claimedBeforeCents: toCents(v.claimed) ?? 0,
            })} />
        )}
      </section>

      <section className="stack">
        <h2>Fittings</h2>
        <p className="muted small">Plant and equipment from the depreciation schedule (carpets, blinds, appliances, hot water). Their decline in value goes in 21U.</p>
        {p.plant.length === 0 ? <p className="muted">None yet.</p> : (
          <div className="card list-card list">
            {p.plant.map((a) => (
              <div key={a.id} className="list-item">
                <div style={{ flex: 1 }}>{a.description}<div className="muted small">{a.effectiveLifeYears} years · {a.method === "prime_cost" ? "prime cost" : "diminishing value"}{a.secondHand ? " · second-hand" : ""}{a.blocked ? " · not depreciable" : ""}</div></div>
                <div style={{ textAlign: "right" }}><span className="amount">{money(a.opening?.valueCents ?? a.costCents)}</span>{a.closingCents !== null && <div className="muted small">{money(a.closingCents)} left</div>}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="stack">
        <h2>Read a document</h2>
        <p className="muted small">Upload the settlement statement or the depreciation schedule, check what was found, and add the rows you agree with.</p>
        <DocumentReader p={p} onDone={setP} />
      </section>

      <section className="stack">
        <h2>Cost base</h2>
        <p className="muted small">What it cost you, for capital gains when you sell. Capital works claimed are taken off automatically.</p>
        <div className="card list-card list">
          <div className="list-item"><span style={{ flex: 1 }}>Purchase price</span><span className="amount">{money(p.purchasePriceCents)}</span></div>
          {p.costBase.map((c) => (
            <div key={c.id} className="list-item"><div style={{ flex: 1 }}>{c.description}<div className="muted small">{ELEMENT_LABEL[c.element]} · {shortDate(c.incurredOn)}</div></div><span className="amount">{money(c.amountCents)}</span></div>
          ))}
        </div>
        {!p.sale && (
          <InlineForm<PropertyDetail> title="Add to the cost base" onDone={setP}
            fields={[
              { id: "element", label: "Kind", type: "select", options: Object.entries(ELEMENT_LABEL).map(([value, label]) => ({ value, label })) },
              { id: "description", label: "What", type: "text" },
              { id: "amount", label: "Amount", type: "text" },
              { id: "date", label: "Date", type: "date" },
            ]}
            submit={(v) => api.post(`/api/properties/${p.id}/cost-base`, { element: Number(v.element), description: v.description, amountCents: needCents(v.amount, "the amount"), incurredOn: v.date })} />
        )}
      </section>

      {!p.sale && (
        <section className="stack">
          <h2>Sold it?</h2>
          <SellForm p={p} onDone={setP} />
        </section>
      )}
    </div>
  );
}

function SellForm({ p, onDone }: { p: PropertyDetail; onDone: (p: PropertyDetail) => void }) {
  const [repaid, setRepaid] = useState<string[]>(p.loans.filter((l) => !l.closedOn).map((l) => l.id));
  return (
    <div className="stack">
      {p.loans.some((l) => !l.closedOn) && (
        <div className="stack small" style={gap(4)}>
          <span className="muted">Loans paid out at settlement:</span>
          {p.loans.filter((l) => !l.closedOn).map((l) => (
            <label key={l.id} className="row" style={{ gap: 8 }}>
              <input type="checkbox" checked={repaid.includes(l.id)} onChange={(e) => setRepaid(e.target.checked ? [...repaid, l.id] : repaid.filter((x) => x !== l.id))} />{l.name}
            </label>
          ))}
        </div>
      )}
      <InlineForm<PropertyDetail> title="Record the sale" onDone={onDone}
        fields={[
          { id: "contractOn", label: "Sale contract date", type: "date", hint: "Decides which year the capital gain is in." },
          { id: "settledOn", label: "Settlement date", type: "date", future: true },
          { id: "price", label: "Sale price", type: "text" },
          { id: "agent", label: "Agent's commission", type: "text", optional: true },
          { id: "selling", label: "Other selling costs (legal, advertising)", type: "text", optional: true },
        ]}
        submit={(v) => api.post(`/api/properties/${p.id}/sell`, {
          contractOn: v.contractOn, settledOn: v.settledOn, priceCents: needCents(v.price, "the sale price"),
          saleCosts: [["Agent's commission", v.agent], ["Other selling costs", v.selling]].flatMap(([description, amount]) => {
            const cents = toCents(amount);
            return cents ? [{ description, amountCents: cents }] : [];
          }),
          loansRepaid: repaid,
        })} />
    </div>
  );
}

// ---- Loans ------------------------------------------------------------------------------------

function PurposeEditor({ properties, value, onChange }: { properties: PropertySummary[]; value: LoanPurpose[]; onChange: (v: LoanPurpose[]) => void }) {
  const options = [
    ...properties.filter((p) => !p.soldOn).map((p) => ({ key: `property:${p.id}`, label: p.name })),
    { key: "shares", label: "Shares and other investments" },
    { key: "private", label: "Private (your home, car, personal)" },
    { key: "other", label: "Other" },
  ];
  const keyOf = (x: LoanPurpose) => (x.type === "property" ? `property:${x.id}` : x.type);
  const fromKey = (key: string, pct: number): LoanPurpose => (key.startsWith("property:") ? { type: "property", id: key.slice(9), pct } : { type: key as LoanPurpose["type"], pct });
  const total = value.reduce((s, x) => s + x.pct, 0);
  return (
    <div className="stack" style={gap(8)}>
      {value.map((x, i) => (
        <div className="fields-2" key={i}>
          <div className="field"><label htmlFor={`purpose-${i}`}>Used for</label>
            <select id={`purpose-${i}`} value={keyOf(x)} onChange={(e) => onChange(value.map((y, j) => (j === i ? fromKey(e.target.value, y.pct) : y)))}>
              {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
          <div className="field"><label htmlFor={`purpose-pct-${i}`}>Share of the loan (%)</label>
            <input id={`purpose-pct-${i}`} type="number" min={1} max={100} value={x.pct} onChange={(e) => onChange(value.map((y, j) => (j === i ? { ...y, pct: Number(e.target.value) } : y)))} />
          </div>
        </div>
      ))}
      <div className="row">
        <button type="button" className="btn" onClick={() => onChange([...value, { type: "private", pct: Math.max(0, 100 - total) }])}>+ Split it</button>
        {value.length > 1 && <button type="button" className="btn ghost" onClick={() => onChange(value.slice(0, -1))}>Remove</button>}
      </div>
      {Math.abs(total - 100) > 0.001 && <div className="notice warn">The split adds up to {total}%. It needs to add up to 100%.</div>}
      <p className="muted small">What the money was used for decides what the interest can be claimed against — not what the loan is secured on.</p>
    </div>
  );
}

export function AddLoan() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [properties, setProperties] = useState<PropertySummary[] | null>(null);
  const [loans, setLoans] = useState<LoanSummary[]>([]);
  const [v, setV] = useState<Record<string, string>>({ yourPct: "100" });
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setV({ ...v, [k]: e.target.value });
  const [purposes, setPurposes] = useState<LoanPurpose[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get<PropertySummary[]>("/api/properties").then((list) => {
      setProperties(list);
      const chosen = params.get("property") ?? list.find((p) => !p.soldOn)?.id;
      setPurposes(chosen ? [{ type: "property", id: chosen, pct: 100 }] : [{ type: "shares", pct: 100 }]);
      const p = list.find((x) => x.id === chosen);
      if (p) setV((old) => ({ ...old, name: `${p.name} loan`, yourPct: String(p.yourSharePct || 100) }));
    }).catch(setError);
    api.get<LoanSummary[]>("/api/loans").then(setLoans).catch(() => undefined);
  }, [params]);
  if (!properties) return <><ErrorNotice error={error} /><Loading /></>;

  const field = (id: string, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}, hint?: string) => (
    <div className="field"><label htmlFor={`l-${id}`}>{label}</label><input id={`l-${id}`} type="text" value={v[id] ?? ""} onChange={set(id)} {...props} />{hint && <span className="hint">{hint}</span>}</div>
  );
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const pct = Number(v.yourPct);
      const costs = [["Loan establishment and application fees", v.establishment], ["Lender's mortgage insurance", v.lmi], ["Mortgage registration and stamp duty", v.registration], ["Other borrowing costs", v.otherCosts]]
        .flatMap(([description, amount]) => {
          const cents = toCents(amount);
          if (Number.isNaN(cents)) throw new Error("Borrowing costs must be numbers");
          return cents ? [{ description, amountCents: cents }] : [];
        });
      const loan = await api.post<LoanDetail>("/api/loans", {
        name: v.name?.trim(), lender: v.lender?.trim() || null, accountHint: v.accountHint?.trim() || null, startedOn: v.startedOn,
        amountCents: needCents(v.amount, "the amount borrowed"), termYears: v.term ? Number(v.term) : null, interestOnly: v.interestOnly === "yes",
        refinancedFrom: v.refinancedFrom || null, purposes,
        owners: { yourPct: pct, others: pct < 100 ? [{ name: v.coOwner?.trim() || "Co-borrower", pct: 100 - pct }] : [] },
        borrowingCosts: costs,
      });
      navigate(`/loans/${loan.id}`, { replace: true });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="wizard stack" style={gap(20)}>
      <div className="row"><Link to="/properties" className="btn ghost">← Properties</Link></div>
      <h1>Add a loan</h1>
      <div className="fields-2">{field("name", "What do you call it?", { placeholder: "e.g. Unit 4 loan" })}{field("lender", "Lender")}</div>
      <div className="fields-2">
        {field("startedOn", "Date the loan started", { type: "date", max: today() }, "Usually the settlement date.")}
        {field("amount", "Amount borrowed", { inputMode: "decimal" })}
      </div>
      <div className="fields-2">
        {field("term", "Term (years)", { type: "number", min: 1, max: 40 })}
        {field("accountHint", "Last 4 digits of the account", { maxLength: 6 }, "Optional, to tell statements apart.")}
      </div>
      {loans.filter((l) => !l.closedOn).length > 0 && (
        <div className="field"><label htmlFor="l-refi">Is it refinancing another loan?</label>
          <select id="l-refi" value={v.refinancedFrom ?? ""} onChange={set("refinancedFrom")}>
            <option value="">No</option>
            {loans.filter((l) => !l.closedOn).map((l) => <option key={l.id} value={l.id}>Yes — it replaces {l.name}</option>)}
          </select>
          <span className="hint">The old loan is closed on this date, and its remaining borrowing costs are claimed this year.</span>
        </div>
      )}
      <h2>What was it used for?</h2>
      <PurposeEditor properties={properties} value={purposes} onChange={setPurposes} />
      <h2>Whose name is it in?</h2>
      <div className="fields-2">
        {field("yourPct", "Your share (%)", { type: "number", min: 1, max: 100 })}
        {Number(v.yourPct) < 100 && field("coOwner", "The other borrower")}
      </div>
      <h2>Borrowing costs</h2>
      <p className="muted small">From the settlement statement or the loan documents. Over $100 in total, they're claimed over 5 years (or the loan term, if shorter).</p>
      <div className="fields-2">{field("establishment", "Establishment and application fees", { inputMode: "decimal" })}{field("lmi", "Lender's mortgage insurance", { inputMode: "decimal" })}</div>
      <div className="fields-2">{field("registration", "Mortgage registration and stamp duty", { inputMode: "decimal" })}{field("otherCosts", "Other (valuation, broker, title search)", { inputMode: "decimal" })}</div>
      <ErrorNotice error={error} />
      <div className="wizard-actions"><div className="spacer" />
        <button type="button" className="btn primary" disabled={busy || !v.name?.trim() || !v.startedOn || !v.amount?.trim() || Math.abs(purposes.reduce((s, x) => s + x.pct, 0) - 100) > 0.001} onClick={save}>{busy ? "Adding…" : "Add loan"}</button>
      </div>
    </div>
  );
}

export function LoanDetailPage() {
  const { id } = useParams();
  const [loan, setLoan] = useState<LoanDetail | null>(null);
  const [properties, setProperties] = useState<PropertySummary[]>([]);
  const [newPurposes, setNewPurposes] = useState<LoanPurpose[] | null>(null);
  const [purposeFrom, setPurposeFrom] = useState("");
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    api.get<LoanDetail>(`/api/loans/${id}`).then(setLoan).catch(setError);
    api.get<PropertySummary[]>("/api/properties").then(setProperties).catch(() => undefined);
  }, [id]);
  if (!loan) return <><ErrorNotice error={error} /><Loading /></>;
  const current = loan.purposeHistory.at(-1);
  return (
    <div className="stack" style={gap(24)}>
      <div className="row wrap"><Link to="/properties" className="btn ghost">← Properties</Link></div>
      <div>
        <h1>{loan.name}</h1>
        <p className="muted">{loan.lender ?? "Loan"}{loan.accountHint ? ` ···${loan.accountHint}` : ""} · {money(loan.amountCents)} from {shortDate(loan.startedOn)}{loan.termYears ? ` over ${loan.termYears} years` : ""}{loan.closedOn ? ` · closed ${shortDate(loan.closedOn)}` : ""}</p>
      </div>
      {loan.warnings.map((w, i) => <div key={i} className="notice warn">{w}</div>)}
      {loan.problem && <div className="notice warn">{loan.problem}</div>}

      <section className="stack">
        <h2>What it's used for</h2>
        <div className="card list-card list">
          {[...loan.purposeHistory].reverse().map((h) => (
            <div key={h.from} className="list-item">
              <div style={{ flex: 1 }}>{h.purposes.map((x) => `${x.name} ${x.pct}%`).join(" · ")}</div>
              <span className="muted small">{shortDate(h.from)} – {h.to ? shortDate(h.to) : "now"}</span>
            </div>
          ))}
        </div>
        {!loan.closedOn && (newPurposes ? (
          <div className="card flat stack" style={gap(12)}>
            <h3>A redraw, top-up or change in use</h3>
            <div className="field" style={{ maxWidth: 260 }}><label htmlFor="purpose-from">From</label><input id="purpose-from" type="date" max={today()} value={purposeFrom} onChange={(e) => setPurposeFrom(e.target.value)} /></div>
            <PurposeEditor properties={properties} value={newPurposes} onChange={setNewPurposes} />
            <div className="row"><div className="spacer" />
              <button type="button" className="btn ghost" onClick={() => setNewPurposes(null)}>Cancel</button>
              <button type="button" className="btn primary" disabled={!purposeFrom} onClick={async () => {
                try {
                  setError(null);
                  setLoan(await api.put<LoanDetail>(`/api/loans/${loan.id}/purpose`, { from: purposeFrom, purposes: newPurposes }));
                  setNewPurposes(null);
                } catch (e) { setError(e); }
              }}>Save</button>
            </div>
            <ErrorNotice error={error} />
          </div>
        ) : <button type="button" className="btn" style={{ alignSelf: "flex-start" }} onClick={() => setNewPurposes(current?.purposes.map(({ type, id: pid, pct }) => ({ type, id: pid, pct })) ?? [])}>Record a redraw or change in use</button>)}
      </section>

      <section className="stack">
        <h2>Interest statements</h2>
        <p className="muted small">Your lender's annual interest statement (usually 1 July – 30 June). The interest is split by what the loan was used for, day by day.</p>
        {loan.statements.length > 0 && (
          <div className="card list-card list">
            {[...loan.statements].reverse().map((s) => (
              <div key={s.id} className="list-item"><span style={{ flex: 1 }}>{shortDate(s.from)} – {shortDate(s.to)}</span><span className="amount">{money(s.interestCents)} interest</span></div>
            ))}
          </div>
        )}
        <InlineForm<LoanDetail> title="Add an interest statement" onDone={setLoan}
          fields={[
            { id: "from", label: "From", type: "date" }, { id: "to", label: "To", type: "date", future: true },
            { id: "interest", label: "Interest charged", type: "text" }, { id: "fees", label: "Fees charged", type: "text", optional: true },
            { id: "balance", label: "Closing balance", type: "text", optional: true },
          ]}
          submit={(v) => api.post(`/api/loans/${loan.id}/statements`, {
            from: v.from, to: v.to, interestCents: needCents(v.interest, "the interest"), feesCents: toCents(v.fees) ?? 0, closingBalanceCents: toCents(v.balance),
          })} />
      </section>

      <section className="stack">
        <h2>Borrowing costs</h2>
        {loan.borrowingCosts.length === 0 ? <p className="muted">None recorded.</p> : (
          <div className="card list-card list">
            {loan.borrowingCosts.map((c) => <div key={c.id} className="list-item"><span style={{ flex: 1 }}>{c.description}</span><span className="amount">{money(c.amountCents)}</span></div>)}
          </div>
        )}
        {loan.borrowingSchedule.length > 0 && (
          <div className="card flat stack" style={gap(6)}>
            {loan.borrowingSchedule.map((b) => <div key={b.fy} className="row"><span className="muted">{b.label}</span><div className="spacer" /><strong>{money(b.deductionCents)}</strong></div>)}
            <p className="muted small">{loan.borrowingSchedule[0]!.explanation} The amount for each property is its share of the loan, at your share.</p>
          </div>
        )}
        <InlineForm<LoanDetail> title="Add a borrowing cost" onDone={setLoan}
          fields={[{ id: "description", label: "What", type: "text" }, { id: "amount", label: "Amount", type: "text" }]}
          submit={(v) => api.post(`/api/loans/${loan.id}/borrowing-costs`, { description: v.description, amountCents: needCents(v.amount, "the amount") })} />
      </section>

      {!loan.closedOn && (
        <section className="stack">
          <h2>Paid it off?</h2>
          <InlineForm<LoanDetail> title="Close the loan" onDone={setLoan}
            fields={[{ id: "closedOn", label: "Date paid out", type: "date", hint: "Any borrowing costs not yet claimed are claimed in that year." }]}
            submit={(v) => api.post(`/api/loans/${loan.id}/close`, { closedOn: v.closedOn })} />
        </section>
      )}
    </div>
  );
}

// ---- Reading a settlement statement or depreciation schedule ---------------------------------

const PROPOSAL_LABEL: Record<SettlementLine["proposal"], string> = {
  cost_base: "Cost base",
  rental_expense: "Rental expense",
  borrowing_cost: "Loan borrowing cost",
  sale_cost: "Selling cost",
  ignore: "Leave out",
};

function DocumentReader({ p, onDone }: { p: PropertyDetail; onDone: (p: PropertyDetail) => void }) {
  const [reading, setReading] = useState<Reading | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [rows, setRows] = useState<(SettlementLine & { use: boolean })[]>([]);
  const [plant, setPlant] = useState<{ description: string; costCents: number; effectiveLifeYears: number; method: "diminishing_value" | "prime_cost"; use: boolean }[]>([]);
  const [works, setWorks] = useState<{ use: boolean; costCents: number; started: string; completed: string }>({ use: false, costCents: 0, started: "", completed: "" });
  const [secondHand, setSecondHand] = useState(false);
  const [date, setDate] = useState(p.settledOn);
  const [loanId, setLoanId] = useState(p.loans[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const show = (r: Reading) => {
    setReading(r);
    if (r.kind === "settlement") {
      setRows(r.reading.lines.map((l) => ({ ...l, use: l.proposal !== "ignore" })));
      if (r.reading.settlementDate) setDate(r.reading.settlementDate);
    } else {
      setPlant(r.reading.plant.map((x) => ({ ...x, use: true })));
      const cw = r.reading.capitalWorks;
      setWorks({ use: Boolean(cw), costCents: cw?.costCents ?? 0, started: cw?.constructionStartedOn ?? "", completed: cw?.completedOn ?? "" });
    }
  };
  const pick = (kind: "settlement" | "schedule" | "csv") => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = kind === "csv" ? ".csv,text/csv" : "application/pdf";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setBusy(true);
      setError(null);
      try {
        if (kind === "csv") {
          setDocumentId(null);
          show(await api.post<Reading>(`/api/properties/${p.id}/read-csv`, { csv: await file.text() }));
        } else {
          const doc = await api.record(file);
          setDocumentId(doc.documentId);
          show(await api.post<Reading>(`/api/properties/${p.id}/read`, { documentId: doc.documentId, kind }));
        }
      } catch (e) {
        setError(e);
      } finally {
        setBusy(false);
      }
    };
    input.click();
  };

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const chosen = rows.filter((r) => r.use);
      if (chosen.some((r) => r.proposal === "borrowing_cost") && !loanId) throw new Error("Add the loan first, so borrowing costs can go on it.");
      const body = reading?.kind === "settlement" ? {
        documentId,
        costBase: chosen.filter((r) => r.proposal === "cost_base" || r.proposal === "sale_cost").map((r) => ({ element: r.element ?? 2, description: r.label, amountCents: r.cents, incurredOn: date, saleCost: r.proposal === "sale_cost" })),
        expenses: chosen.filter((r) => r.proposal === "rental_expense").map((r) => ({ categoryCode: r.categoryCode ?? "rental.other", description: r.label, amountCents: r.cents, date })),
        borrowingCosts: chosen.filter((r) => r.proposal === "borrowing_cost").map((r) => ({ loanId, description: r.label, amountCents: r.cents })),
      } : {
        documentId,
        capitalWorks: works.use ? { description: "The building", costCents: works.costCents, constructionStartedOn: works.started, completedOn: works.completed || works.started } : null,
        plant: plant.filter((x) => x.use).map(({ use: _use, ...x }) => ({ ...x, acquiredOn: date, secondHand })),
      };
      if (reading?.kind === "schedule" && works.use && !works.started) throw new Error("Enter when construction started (it's in the report's summary).");
      onDone(await api.post<PropertyDetail>(`/api/properties/${p.id}/apply`, body));
      setReading(null);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  if (!reading) {
    return (
      <div className="stack">
        <div className="row wrap">
          <button type="button" className="btn" disabled={busy} onClick={() => pick("settlement")}>Settlement statement (PDF)</button>
          <button type="button" className="btn" disabled={busy} onClick={() => pick("schedule")}>Depreciation schedule (PDF)</button>
          <button type="button" className="btn ghost" disabled={busy} onClick={() => pick("csv")}>Depreciation schedule (CSV)</button>
        </div>
        {busy && <span className="muted small">Reading…</span>}
        <ErrorNotice error={error} />
      </div>
    );
  }

  return (
    <div className="card stack" style={gap(12)}>
      {reading.kind === "settlement" ? (
        <>
          <h3>Settlement statement{reading.reading.kind !== "unknown" ? ` (${reading.reading.kind})` : ""}</h3>
          {reading.reading.priceCents !== null && <p className="muted small">Price on the statement: {money(reading.reading.priceCents)}{reading.reading.priceCents !== (p.sale?.priceCents ?? p.purchasePriceCents) ? " — different from what's recorded; check it." : "."}</p>}
          <div className="field" style={{ maxWidth: 240 }}><label htmlFor="read-date">Settlement date</label><input id="read-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          {rows.length === 0 && <p className="muted">No costs were recognised. Add them by hand below.</p>}
          {rows.map((r, i) => (
            <label key={i} className="row" style={{ gap: 10, alignItems: "flex-start" }}>
              <input type="checkbox" checked={r.use} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, use: e.target.checked } : x)))} />
              <div style={{ flex: 1 }}>
                <div className="row"><span>{r.label}</span><div className="spacer" /><span className="amount">{money(r.cents)}</span></div>
                <div className="muted small">{PROPOSAL_LABEL[r.proposal]} — {r.why}</div>
              </div>
            </label>
          ))}
          {rows.some((r) => r.use && r.proposal === "borrowing_cost") && p.loans.length > 0 && (
            <div className="field" style={{ maxWidth: 320 }}><label htmlFor="read-loan">Borrowing costs go on</label>
              <select id="read-loan" value={loanId} onChange={(e) => setLoanId(e.target.value)}>{p.loans.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
            </div>
          )}
        </>
      ) : (
        <>
          <h3>Depreciation schedule</h3>
          {reading.reading.notes.map((n, i) => <div key={i} className="notice">{n}</div>)}
          <label className="row" style={{ gap: 10 }}>
            <input type="checkbox" checked={works.use} onChange={(e) => setWorks({ ...works, use: e.target.checked })} />
            <span style={{ flex: 1 }}>Capital works (the building)</span>
            <input type="text" inputMode="decimal" style={{ maxWidth: 160 }} aria-label="Construction cost" value={works.costCents ? String(works.costCents / 100) : ""} onChange={(e) => setWorks({ ...works, costCents: toCents(e.target.value) ?? 0, use: true })} />
          </label>
          {works.use && (
            <div className="fields-2">
              <div className="field"><label htmlFor="cw-start">Construction started</label><input id="cw-start" type="date" value={works.started} onChange={(e) => setWorks({ ...works, started: e.target.value })} /></div>
              <div className="field"><label htmlFor="cw-done">Construction finished</label><input id="cw-done" type="date" value={works.completed} onChange={(e) => setWorks({ ...works, completed: e.target.value })} /></div>
            </div>
          )}
          {plant.map((x, i) => (
            <label key={i} className="row" style={{ gap: 10 }}>
              <input type="checkbox" checked={x.use} onChange={(e) => setPlant(plant.map((y, j) => (j === i ? { ...y, use: e.target.checked } : y)))} />
              <span style={{ flex: 1 }}>{x.description}<span className="muted small"> · {x.effectiveLifeYears} years · {x.method === "prime_cost" ? "PC" : "DV"}</span></span>
              <span className="amount">{money(x.costCents)}</span>
            </label>
          ))}
          <div className="fields-2">
            <div className="field"><label htmlFor="plant-date">Fittings held from</label><input id="plant-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} /><span className="hint">Usually the settlement date.</span></div>
            <label className="row small" style={{ gap: 8, alignSelf: "center" }}>
              <input type="checkbox" checked={secondHand} onChange={(e) => setSecondHand(e.target.checked)} />
              The fittings came with an established home (second-hand)
            </label>
          </div>
        </>
      )}
      <ErrorNotice error={error} />
      <div className="row"><div className="spacer" />
        <button type="button" className="btn ghost" onClick={() => setReading(null)}>Cancel</button>
        <button type="button" className="btn primary" disabled={busy} onClick={apply}>{busy ? "Adding…" : "Add the ticked rows"}</button>
      </div>
    </div>
  );
}
